import { Injectable, NgZone, inject } from '@angular/core';
import * as Sentry from '@sentry/angular';

export const PAGE_LOAD_TRACE_TIMEOUT_MS = 60_000;

export type PageLoadSpanAttributes = Record<string, string | number | boolean>;

export interface PageLoadTrace {
  setAttributes(attributes: PageLoadSpanAttributes): void;
  complete(attributes?: PageLoadSpanAttributes): void;
  fail(error: unknown, attributes?: PageLoadSpanAttributes): void;
  cancel(): void;
}

@Injectable({ providedIn: 'root' })
export class PageLoadTracingService {
  private readonly ngZone = inject(NgZone);

  start(pageName: string, routePattern: string, attributes: PageLoadSpanAttributes = {}): PageLoadTrace {
    return createPageLoadTrace(
      pageName,
      routePattern,
      attributes,
      task => this.ngZone.runOutsideAngular(task),
    );
  }
}

export function createPageLoadTrace(
  pageName: string,
  routePattern: string,
  attributes: PageLoadSpanAttributes = {},
  runOutsideAngular: <T>(task: () => T) => T = task => task(),
): PageLoadTrace {
  const activeSpan = Sentry.getActiveSpan();
  const parentSpan = activeSpan ? Sentry.getRootSpan(activeSpan) : undefined;
  const span = Sentry.startInactiveSpan({
    name: `${pageName} content ready`,
    op: 'ui.page.load',
    onlyIfParent: true,
    parentSpan,
    attributes: {
      'page.name': pageName,
      'page.route': routePattern,
      'page.load.timeout_ms': PAGE_LOAD_TRACE_TIMEOUT_MS,
      ...getNetworkAttributes(),
      ...attributes,
    },
  });

  return new SentryPageLoadTrace(span, runOutsideAngular);
}

class SentryPageLoadTrace implements PageLoadTrace {
  private ended = false;
  private completionScheduled = false;
  private deadlineTimer?: ReturnType<typeof setTimeout>;
  private paintFallbackTimer?: ReturnType<typeof setTimeout>;
  private animationFrameId?: number;

  constructor(
    private readonly span: Sentry.Span,
    private readonly runOutsideAngular: <T>(task: () => T) => T,
  ) {
    if (!span.isRecording()) {
      this.ended = true;
      return;
    }

    this.runOutsideAngular(() => {
      this.deadlineTimer = setTimeout(() => {
        this.finish({ code: 2, message: 'deadline_exceeded' }, 'timeout');
      }, PAGE_LOAD_TRACE_TIMEOUT_MS);
    });
  }

  setAttributes(attributes: PageLoadSpanAttributes): void {
    if (!this.ended) {
      this.span.setAttributes(attributes);
    }
  }

  complete(attributes: PageLoadSpanAttributes = {}): void {
    if (this.ended || this.completionScheduled) {
      return;
    }

    this.span.setAttributes(attributes);
    this.completionScheduled = true;
    this.runOutsideAngular(() => {
      const finish = () => this.finish({ code: 1 }, 'success');

      if (typeof requestAnimationFrame === 'function' && document.visibilityState !== 'hidden') {
        this.paintFallbackTimer = setTimeout(finish, 1_000);
        this.animationFrameId = requestAnimationFrame(finish);
      } else {
        this.paintFallbackTimer = setTimeout(finish, 0);
      }
    });
  }

  fail(error: unknown, attributes: PageLoadSpanAttributes = {}): void {
    if (this.ended) {
      return;
    }

    this.span.setAttributes({
      ...attributes,
      'error.type': getErrorType(error),
    });
    this.finish({ code: 2, message: 'internal_error' }, 'error');
  }

  cancel(): void {
    this.finish({ code: 2, message: 'cancelled' }, 'cancelled');
  }

  private finish(status: { code: 1 } | { code: 2; message: string }, result: string): void {
    if (this.ended) {
      return;
    }

    this.ended = true;
    this.clearTimers();
    this.span.setAttribute('page.load.result', result);
    this.span.setStatus(status);
    this.span.end();
  }

  private clearTimers(): void {
    if (this.deadlineTimer !== undefined) {
      clearTimeout(this.deadlineTimer);
    }
    if (this.paintFallbackTimer !== undefined) {
      clearTimeout(this.paintFallbackTimer);
    }
    if (this.animationFrameId !== undefined && typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(this.animationFrameId);
    }
  }
}

function getNetworkAttributes(): PageLoadSpanAttributes {
  const connection = (navigator as Navigator & {
    connection?: {
      downlink?: number;
      effectiveType?: string;
      rtt?: number;
      saveData?: boolean;
    };
  }).connection;

  return {
    'browser.network.online': navigator.onLine,
    ...(connection?.effectiveType ? { 'browser.network.effective_type': connection.effectiveType } : {}),
    ...(typeof connection?.downlink === 'number' ? { 'browser.network.downlink_mbps': connection.downlink } : {}),
    ...(typeof connection?.rtt === 'number' ? { 'browser.network.rtt_ms': connection.rtt } : {}),
    ...(typeof connection?.saveData === 'boolean' ? { 'browser.network.save_data': connection.saveData } : {}),
  };
}

function getErrorType(error: unknown): string {
  if (error instanceof Error) {
    return error.name;
  }

  return typeof error;
}
